// RECALL-RULES: a `memory_rule` approval ("Standing rule"): who it binds (every agent, or one DE),
// its title, text and why. The approver may edit the title and text only ("Edit and approve").
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  Input,
  Textarea,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PencilIcon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import type { ApprovalRow } from "../../api/types";
import type { CompanyAccess } from "../company";
import { problemText } from "../de/de-status";
import { Facts } from "../screen-parts";
import { invalidateDecided } from "./agent-queries";

export type MemoryRuleChange = {
  rule_id: string;
  scope: "company" | "de";
  de_membership_id: string | null;
  de_name: string | null;
  title: string;
  text: string;
  why: string | null;
  source: string;
};

export const memoryRuleChangeOf = (change: Record<string, unknown>) =>
  change as unknown as MemoryRuleChange;

export function MemoryRulePreview({ change: c }: { change: MemoryRuleChange }) {
  return (
    <div data-slot="approval-memory-rule" className="flex flex-col gap-2 text-sm">
      <Facts
        items={[
          {
            label: "Applies to",
            value:
              c.scope === "de" ? (
                <span className="inline-flex items-center gap-1">
                  {workerName(c.de_name ?? "A DE")}
                  <WorkerBadge kind="de" size="sm" />
                </span>
              ) : (
                "Every agent"
              ),
          },
          { label: "Title", value: c.title },
          { label: "Rule", value: <span className="whitespace-pre-line">{c.text}</span> },
          ...(c.why ? [{ label: "Why", value: c.why }] : []),
        ]}
      />
    </div>
  );
}

/** "Edit and approve" for a standing rule: the title and text only. */
export function MemoryRuleEditDialog({
  api,
  company,
  approval: a,
  size = "default",
}: {
  api: Api;
  company: CompanyAccess;
  approval: ApprovalRow;
  size?: "default" | "lg";
}) {
  const qc = useQueryClient();
  const c = memoryRuleChangeOf(a.proposal.change);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(c.title);
  const [text, setText] = useState(c.text);
  const ids = { title: useId(), text: useId() };
  const ok = title.trim().length > 0 && title.trim().length <= 120 && text.trim().length > 0;
  const edit = useMutation({
    mutationFn: () =>
      api.client.approvals.edit({
        params: { co: company.id, id: a.id },
        body: {
          proposal: {
            ...a.proposal,
            change: { ...a.proposal.change, title: title.trim(), text: text.trim() },
          },
        },
      }),
    onSuccess: () => setOpen(false),
    onSettled: () => invalidateDecided(qc, company.id),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="secondary" size={size} onClick={() => setOpen(true)}>
        <PencilIcon aria-hidden="true" />
        Edit and approve
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit rule</DialogTitle>
        </DialogHeader>
        <Field>
          <FieldLabel htmlFor={ids.title}>Title</FieldLabel>
          <Input
            id={ids.title}
            maxLength={120}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor={ids.text}>Rule</FieldLabel>
          <Textarea
            id={ids.text}
            rows={4}
            maxLength={1000}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </Field>
        {edit.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {problemText(edit.error, "The rule could not be saved.")}
          </p>
        ) : null}
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
          <Button disabled={!ok || edit.isPending} onClick={() => edit.mutate()}>
            {edit.isPending ? "Saving…" : "Approve with edits"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
